import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { manageUsersService } from '../services/manageUsers';
import { ManageUserUpdateRequest, ManageUserStatusRequest, ManageUserBillingTypeRequest } from '../types/manageUsers';

// Query Keys
const manageUsersKeys = {
  all: ['manage-users'] as const,
  lists: () => [...manageUsersKeys.all, 'list'] as const,
  list: (params: { page: number; size: number; search: string }) =>
    [...manageUsersKeys.lists(), params] as const,
  details: () => [...manageUsersKeys.all, 'detail'] as const,
  detail: (userId: string | number) => [...manageUsersKeys.details(), userId] as const,
  documents: (userId: string | number) => [...manageUsersKeys.all, 'documents', userId] as const,
};

export const useManageUsers = () => {
  const queryClient = useQueryClient();

  // 🔹 GET users
  const useUsersQuery = (page: number, size: number, search: string = '') => {
    return useQuery({
      queryKey: manageUsersKeys.list({ page, size, search }),
      queryFn: () => manageUsersService.fetchUsers(page, size, search),
      staleTime: 2 * 60 * 1000,
      retry: (count, error: any) => {
        if (error?.response?.status === 401) return false;
        return count < 2;
      },
    });
  };

  // 🔹 GET user details
  const useUserDetailQuery = (userId: string | number, options?: { enabled?: boolean }) => {
    return useQuery({
      queryKey: manageUsersKeys.detail(userId),
      queryFn: () => manageUsersService.fetchUserDetail(userId),
      enabled: !!userId && (options?.enabled ?? true),
      staleTime: 2 * 60 * 1000,
    });
  };

  // 🔹 UPDATE user
  const useUpdateUserMutation = () => {
    return useMutation({
      mutationFn: ({ userId, payload }: { userId: string | number; payload: ManageUserUpdateRequest }) =>
        manageUsersService.updateUser(userId, payload),
      onSuccess: (_, { userId }) => {
        queryClient.invalidateQueries({ queryKey: manageUsersKeys.detail(userId) });
        queryClient.invalidateQueries({ queryKey: manageUsersKeys.lists() });
      },
    });
  };

  // 🔹 UPDATE user status
  const useUpdateUserStatusMutation = () => {
    return useMutation({
      mutationFn: ({ userId, payload }: { userId: string | number; payload: ManageUserStatusRequest }) =>
        manageUsersService.updateUserStatus(userId, payload),
      onSuccess: (_, { userId }) => {
        queryClient.invalidateQueries({ queryKey: manageUsersKeys.detail(userId) });
        queryClient.invalidateQueries({ queryKey: manageUsersKeys.lists() });
      },
    });
  };

  // 🔹 UPDATE user billing type
  const useUpdateUserBillingTypeMutation = () => {
    return useMutation({
      mutationFn: ({ userId, payload }: { userId: string | number; payload: ManageUserBillingTypeRequest }) =>
        manageUsersService.updateUserBillingType(userId, payload),
      onSuccess: (_, { userId }) => {
        queryClient.invalidateQueries({ queryKey: manageUsersKeys.detail(userId) });
        queryClient.invalidateQueries({ queryKey: manageUsersKeys.lists() });
      },
    });
  };

  // 🔹 GET user documents
  const useUserDocumentsQuery = (userId: string | number, options?: { enabled?: boolean }) => {
    return useQuery({
      queryKey: manageUsersKeys.documents(userId),
      queryFn: () => manageUsersService.fetchUserDocuments(userId),
      enabled: !!userId && (options?.enabled ?? true),
      staleTime: 2 * 60 * 1000,
    });
  };

  return {
    useUsersQuery,
    useUserDetailQuery,
    useUpdateUserMutation,
    useUpdateUserStatusMutation,
    useUpdateUserBillingTypeMutation,
    useUserDocumentsQuery,
  };
};
